import { forwardRef, useRef, useState, type KeyboardEvent } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { KbdHint } from '@/components/ui';

export interface QuickCaptureInputProps {
  onCapture: (text: string) => void;
  className?: string;
  autoFocus?: boolean;
}

export const QuickCaptureInput = forwardRef<HTMLInputElement, QuickCaptureInputProps>(
  function QuickCaptureInput({ onCapture, className, autoFocus }, ref) {
    const [value, setValue] = useState('');
    const innerRef = useRef<HTMLInputElement | null>(null);

    function setRef(el: HTMLInputElement | null) {
      innerRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
    }

    function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'Enter') {
        const trimmed = value.trim();
        if (!trimmed) return;
        onCapture(trimmed);
        setValue('');
      } else if (e.key === 'Escape') {
        setValue('');
        innerRef.current?.blur();
      }
    }

    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border border-line-2 bg-bg px-2.5 py-1.5',
          'focus-within:border-line-3 focus-within:bg-card',
          'focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-2 focus-within:ring-offset-canvas',
          className,
        )}
      >
        <Plus className="h-3.5 w-3.5 shrink-0 text-t3" aria-hidden />
        <input
          ref={setRef}
          data-quick-capture-input
          type="text"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add task…"
          aria-label="Add task"
          spellCheck={false}
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-body text-t1 placeholder:text-t2 outline-none"
        />
        {/* The shortcut hint is only meaningful before typing starts — hide it
            once there's content so long task text never crowds the pill. */}
        {value === '' && <KbdHint className="shrink-0">N</KbdHint>}
      </div>
    );
  },
);
